import React, { useState } from "react";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormHelperText,
  Stack,
  Switch,
  Typography,
} from "@mui/material";
import { InstanceHostSelection } from "../host_selection/Instance";
import { HostedMeetingFallbackSection } from "../meeting_details/HostedMeetingFallbackSection";
import {
  PartnerAssociationSection,
  PartnerAssociationHandle,
} from "../meeting_details/PartnerAssociationSection";
import { Meeting } from "../../../../model/meeting";
import { useBoundStore } from "../../../../store/store";
import { Host } from "../../../../model/host";
import { MeetingTypeInfoSelect } from "../MeetingTypeInfoSelect";
import { ContinuousRoomKeyField } from "./ContinuousRoomKeyField";
import {
  findKeyUsage,
  roomKeyConflictMessage,
} from "../../../../api/continuous_room_key_scope";
import { ApplyScope, ApplyScopeDialog } from "./ApplyScopeDialog";
import { MeetingHostSchedule } from "../../../../model/meeting_host_schedule";
import { useIsMobile } from "../../../../hooks/useIsMobile";

interface Props {
  meeting: Meeting;
  open: boolean;
  onClose: () => void;
}

export const InstanceMetadataEditDialog: React.FC<Props> = ({
  meeting: originalMeeting,
  open,
  onClose,
}) => {
  const setInstanceMetadata = useBoundStore(
    (state) => state.setInstanceMetadata,
  );
  const setRecurringMetadata = useBoundStore(
    (state) => state.setRecurringMetadata,
  );
  // Every meeting the admin can see, with its key — the room-key conflict
  // check reads these instead of fetching per meeting.
  const meetings = useBoundStore((state) => state.meetings);
  const [currentMeeting, setCurrentMeeting] = useState(originalMeeting);
  const [scopePromptOpen, setScopePromptOpen] = useState(false);
  const isMobile = useIsMobile();

  const recurringMeeting = originalMeeting.recurringMeeting;
  const isRecurring = recurringMeeting !== undefined;
  const partnerRef = React.useRef<PartnerAssociationHandle>(null);

  const applyChanges = React.useCallback(
    async (scope: ApplyScope) => {
      // Validate the partner section BEFORE persisting anything — a bad
      // selection there must not leave the metadata half-saved.
      partnerRef.current?.validate();

      // A continuous room key makes meetings share ONE chat. They stay
      // independent meetings, but the shared chat means a key must never mix a
      // partner's private meeting with a public one, or with another partner's
      // — whoever joins through the public meeting lands in the private
      // community's chat. Impossible to unwind afterwards, so it is refused
      // here, at authoring time.
      const conflict = roomKeyConflictMessage(
        await findKeyUsage(
          currentMeeting.metadata.continuousRoomKey ?? "",
          meetings.map((m) => ({
            gcalEventId: m.id,
            recurringMeetingId: m.recurringMeeting?.id,
            continuousRoomKey: m.metadata.continuousRoomKey,
          })),
          {
            gcalEventId: currentMeeting.id,
            recurringMeetingId: recurringMeeting?.id,
          },
        ),
        partnerRef.current?.desiredPartnerId() ?? null,
      );
      if (conflict) {
        partnerRef.current?.reportError(conflict);
        throw new Error(conflict);
      }
      // Partner scope travels with the metadata rather than as its own PATCH:
      // the calendar event is the source of truth (the sync stamps every
      // occurrence from it), and going through this write is what makes the
      // "All events" choice reach occurrences that do not exist yet.
      // "" clears — see InstanceMetadata.partnerId.
      const partnerId = partnerRef.current?.desiredPartnerId() ?? "";

      if (scope === ApplyScope.Recurring && recurringMeeting) {
        // "All events" — push form values up to the master. Per-day host
        // overrides on the series are kept; only `default` is updated when
        // the form has a host. Use the Edit Recurrence dialog for full
        // schedule editing.
        const existingSchedule =
          recurringMeeting.metadata.hostSchedule ?? ({} as MeetingHostSchedule);
        const nextSchedule: MeetingHostSchedule | undefined = currentMeeting
          .metadata.host
          ? { ...existingSchedule, default: currentMeeting.metadata.host }
          : recurringMeeting.metadata.hostSchedule;

        await setRecurringMetadata(recurringMeeting.id, {
          meetingType: currentMeeting.metadata.meetingType,
          memberVisible: currentMeeting.metadata.memberVisible,
          hostSchedule: nextSchedule,
          continuousRoomKey: currentMeeting.metadata.continuousRoomKey,
          partnerId,
        });
      } else {
        await setInstanceMetadata(currentMeeting.id, {
          ...currentMeeting.metadata,
          partnerId,
        });
      }
      // Persist the partner association under the same Save (throws → the
      // dialog stays open and the section shows an error toast).
      await partnerRef.current?.save();
      onClose();
    },
    [
      currentMeeting,
      recurringMeeting,
      meetings,
      setInstanceMetadata,
      setRecurringMetadata,
      onClose,
    ],
  );

  const handleSave = React.useCallback(async () => {
    if (isRecurring) {
      setScopePromptOpen(true);
      return;
    }
    await applyChanges(ApplyScope.Instance);
  }, [isRecurring, applyChanges]);

  const handleScopeChosen = React.useCallback(
    async (scope: ApplyScope) => {
      setScopePromptOpen(false);
      await applyChanges(scope);
    },
    [applyChanges],
  );

  return (
    <>
      <Dialog
        maxWidth="sm"
        fullWidth
        fullScreen={isMobile}
        open={open}
        onClose={onClose}
      >
        <DialogTitle>
          <Stack gap={0.5}>
            {"Editing "}
            {currentMeeting.title}
            {isRecurring && (
              <Typography variant="caption">
                You're editing a single instance of a recurring meeting. On
                save, you'll be asked whether to apply changes to this event
                only or to the entire series.
              </Typography>
            )}
          </Stack>
        </DialogTitle>

        <DialogContent>
          <InstanceHostSelection
            value={currentMeeting.metadata.host}
            onChange={(value: Host) => {
              setCurrentMeeting({
                ...currentMeeting,
                metadata: { ...currentMeeting.metadata, host: value },
              });
            }}
          />

          <MeetingTypeInfoSelect
            currentMeetingTypeId={currentMeeting.metadata.meetingType}
            onMeetingTypeChange={(meetingTypeId: string) => {
              setCurrentMeeting({
                ...currentMeeting,
                metadata: {
                  ...currentMeeting.metadata,
                  meetingType: meetingTypeId,
                },
              });
            }}
            formControlProps={{ fullWidth: true, margin: "normal" }}
            selectProps={{ label: "Meeting Type" }}
          />

          <ContinuousRoomKeyField
            value={currentMeeting.metadata.continuousRoomKey}
            onChange={(value: string) => {
              setCurrentMeeting({
                ...currentMeeting,
                metadata: {
                  ...currentMeeting.metadata,
                  continuousRoomKey: value,
                },
              });
            }}
          />

          <FormControl fullWidth margin="normal">
            <FormControlLabel
              control={
                <Switch
                  checked={currentMeeting.metadata.memberVisible ?? false}
                  onChange={() =>
                    setCurrentMeeting({
                      ...currentMeeting,
                      metadata: {
                        ...currentMeeting.metadata,
                        memberVisible: !currentMeeting.metadata.memberVisible,
                      },
                    })
                  }
                />
              }
              label="Visible to Members"
            />
            <FormHelperText>
              When checked, members are able to see and subscribe to this
              meeting
            </FormHelperText>
          </FormControl>

          <HostedMeetingFallbackSection gcalEventId={currentMeeting.id} />

          <PartnerAssociationSection
            ref={partnerRef}
            gcalEventId={currentMeeting.id}
            recurringMeetingId={recurringMeeting?.id}
            partnerId={currentMeeting.metadata.partnerId}
          />
        </DialogContent>

        <DialogActions>
          <Button onClick={onClose} color="primary">
            Cancel
          </Button>
          <Button onClick={handleSave} color="primary">
            Save
          </Button>
        </DialogActions>
      </Dialog>

      <ApplyScopeDialog
        open={scopePromptOpen}
        onCancel={() => setScopePromptOpen(false)}
        onChoose={handleScopeChosen}
      />
    </>
  );
};
